import axios from 'axios';
import { isTokenExpired, readResponseAuthHeader, storeAuthTokensFromResponse } from './authToken';
import { isUserIdle, startIdleTracker, touchActivity } from './idleTracker';
import { CUSTOMER_API_PATHS, RESTAURANT_API_PATHS } from '../constants/apiPaths';

const COMPANY_REFRESH_PATH = RESTAURANT_API_PATHS.authRefresh;
const CUSTOMER_REFRESH_PATH = CUSTOMER_API_PATHS.eatsAppAuthRefresh;
const TOKEN_CHECK_INTERVAL_MS = 30 * 1000;
const EATS_APP_DOMAIN = 'EATS_APP';

let refreshPromise = null;
let tokenCheckTimer = null;
let sessionInitialized = false;

const AUTH_STORAGE_KEYS = [
  'Token',
  'RefreshToken',
  'user',
  'userId',
  'organizationId',
  'organizationName',
  'restaurantId',
  'restaurantName',
  'headerEntityType',
  'storeId',
  'storeName',
  'isAuthenticated',
];

function getCompanyApiBaseUrl() {
  return process.env.REACT_APP_API_COMPANY_URL || '';
}

function getCustomerApiBaseUrl() {
  return process.env.REACT_APP_API_CUSTOMER_URL || '';
}

function getStoredUserDomain() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.domain || null;
  } catch {
    return null;
  }
}

function isEatsAppSession() {
  // Partner login always uses company API tokens (including EAP contexts).
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (String(user?.authSource || '').toLowerCase() === 'partner') {
      return false;
    }
  } catch {
    // ignore
  }
  return getStoredUserDomain() === EATS_APP_DOMAIN;
}

function getRefreshConfig() {
  if (isEatsAppSession()) {
    return {
      baseURL: getCustomerApiBaseUrl(),
      refreshPath: CUSTOMER_REFRESH_PATH,
    };
  }

  return {
    baseURL: getCompanyApiBaseUrl(),
    refreshPath: COMPANY_REFRESH_PATH,
  };
}

function isAuthPage(pathname = window.location.pathname) {
  return (
    pathname === '/partner/login' ||
    pathname === '/partner/choose-business' ||
    pathname === '/restaurant/signin' ||
    pathname === '/restaurant/signup' ||
    pathname === '/restaurant/forgotpassword' ||
    pathname === '/organization/signin' ||
    pathname === '/organization/signup' ||
    pathname === '/organization/forgotpassword' ||
    pathname === '/eats-business/login' ||
    pathname === '/eats-business/admin/login' ||
    pathname === '/eats-app/user/login' ||
    pathname === '/eats-app/admin/login'
  );
}

export function getLogoutRedirectPath(pathname = window.location.pathname) {
  if (pathname.startsWith('/organization')) {
    return '/partner/login';
  }
  if (
    pathname.startsWith('/eats-business') ||
    pathname.startsWith('/eats-app') ||
    pathname.startsWith('/app/') ||
    pathname.startsWith('/partner')
  ) {
    return '/partner/login';
  }
  return '/partner/login';
}

/** True when an access token is present (session may still need refresh). */
export function hasActiveSession() {
  return Boolean(localStorage.getItem('Token'));
}

/** Stale UI flags left behind after token was cleared without a full logout. */
function hasOrphanSessionFlags() {
  return (
    localStorage.getItem('isAuthenticated') === 'true' ||
    Boolean(localStorage.getItem('user')) ||
    Boolean(localStorage.getItem('restaurantId')) ||
    Boolean(localStorage.getItem('storeId')) ||
    Boolean(localStorage.getItem('organizationId'))
  );
}

export function performLogout(options = {}) {
  const { redirect = true, redirectPath } = options;
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem('lastActivityAt');

  if (!redirect || typeof window === 'undefined') {
    return;
  }

  const target = redirectPath || getLogoutRedirectPath();
  if (window.location.pathname !== target) {
    window.location.replace(target);
  }
}

async function requestRefreshToken() {
  const refreshToken = localStorage.getItem('RefreshToken');
  if (!refreshToken) {
    throw new Error('Refresh token missing');
  }

  const { baseURL, refreshPath } = getRefreshConfig();
  const client = axios.create({
    baseURL,
    timeout: 20000,
    headers: {
      Authorization: `Bearer ${refreshToken}`,
    },
  });

  const response = await client.post(refreshPath, null, { skipAuthRefresh: true });
  const accessToken = readResponseAuthHeader(response, 'X-Auth-Token');
  if (!accessToken) {
    throw new Error('Refresh response missing access token');
  }

  localStorage.setItem('Token', accessToken);
  touchActivity();
  return accessToken;
}

export async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = requestRefreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function handleTokenExpiry(options = {}) {
  const token = localStorage.getItem('Token');
  if (!token || !isTokenExpired(token)) {
    return { action: 'none' };
  }

  if (isUserIdle()) {
    performLogout(options);
    return { action: 'logout' };
  }

  try {
    await refreshAccessToken();
    return { action: 'refresh' };
  } catch {
    performLogout(options);
    return { action: 'logout' };
  }
}

function scheduleTokenExpiryCheck() {
  if (tokenCheckTimer) {
    clearInterval(tokenCheckTimer);
  }

  tokenCheckTimer = window.setInterval(() => {
    if (isAuthPage()) {
      return;
    }
    if (!localStorage.getItem('Token')) {
      // Ghost session: shell/data left in localStorage but no access token
      if (hasOrphanSessionFlags()) {
        performLogout();
      }
      return;
    }
    handleTokenExpiry();
  }, TOKEN_CHECK_INTERVAL_MS);
}

export function initSessionManager() {
  if (sessionInitialized || typeof window === 'undefined') {
    return;
  }

  startIdleTracker();
  scheduleTokenExpiryCheck();
  sessionInitialized = true;

  if (isAuthPage()) {
    return;
  }

  if (!localStorage.getItem('Token')) {
    if (hasOrphanSessionFlags()) {
      performLogout();
    }
    return;
  }

  handleTokenExpiry();
}

export function storeLoginTokens(response) {
  storeAuthTokensFromResponse(response);
  touchActivity();
}

export function attachAuthInterceptors(axiosInstance) {
  axiosInstance.interceptors.request.use(async (config) => {
    if (config.skipAuthRefresh) {
      return config;
    }

    const url = String(config.url || '');
    const isRefreshRequest = url.includes('/auth/refresh');
    const token = localStorage.getItem('Token');

    if (token && isTokenExpired(token) && !isRefreshRequest && !isAuthPage()) {
      const result = await handleTokenExpiry({ redirect: false });
      if (result.action === 'logout') {
        performLogout();
        return Promise.reject(new Error('Session expired'));
      }
    }

    const activeToken = localStorage.getItem('Token');
    if (activeToken) {
      const value = `Bearer ${activeToken}`;
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('Authorization', value);
      } else {
        config.headers = config.headers ?? {};
        config.headers.Authorization = value;
      }
    }

    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const originalRequest = error?.config;

      if (
        status !== 401 ||
        !originalRequest ||
        originalRequest._retry ||
        originalRequest.skipAuthRefresh ||
        isAuthPage()
      ) {
        return Promise.reject(error);
      }

      if (isUserIdle()) {
        performLogout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers ?? {};
        if (typeof originalRequest.headers.set === 'function') {
          originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
        } else {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        performLogout();
        return Promise.reject(refreshError);
      }
    }
  );
}
